import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    ChevronDown,
    LogOut,
    User,
    Settings,
    Users,
    ChartColumnIncreasing,
    Bug,
} from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SentryFeedbackButton } from "@/components/ui/sentry-feedback-button";

interface UserMenuProps {
    collapsed?: boolean;
    userAvatarUrl?: string;
    userName?: string;
    onSignOut?: () => void;
    className?: string;
}

export function UserMenu({
    collapsed = false,
    userAvatarUrl,
    userName = "User",
    onSignOut,
    className,
}: UserMenuProps) {
    const getInitials = (name: string) => {
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    className={cn(
                        "flex items-center rounded-lg px-3 py-2 text-md-on-surface transition-all hover:bg-md-surface-level-2 w-full",
                        collapsed ? "justify-center px-2 gap-0" : "gap-3",
                        className,
                    )}
                >
                    <Avatar className="h-10 w-10 shrink-0 shadow-md">
                        <AvatarImage src={userAvatarUrl} />
                        <AvatarFallback className="bg-md-tertiary-container text-md-on-tertiary-container font-medium">
                            {userName ? (
                                getInitials(userName)
                            ) : (
                                <User className="h-4 w-4" />
                            )}
                        </AvatarFallback>
                    </Avatar>
                    <div
                        className={cn(
                            "flex items-center gap-2 min-w-0 transition-all duration-300",
                            collapsed ? "w-0 opacity-0" : "flex-1 opacity-100",
                        )}
                    >
                        <span className="text-sm font-medium truncate">
                            {userName}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                    </div>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align={collapsed ? "end" : "start"}
                className="w-56"
            >
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuItem>
                    <User className="mr-2 h-4 w-4" />
                    <span>Profile</span>
                </DropdownMenuItem>
                <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Administration</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                    <Link to="/users">
                        <Users className="mr-2 h-4 w-4" />
                        <span>Users</span>
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                    <Link to="/usage">
                        <ChartColumnIncreasing className="mr-2 h-4 w-4" />
                        <span>Usage</span>
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Feedback</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                    <SentryFeedbackButton className="w-full justify-start p-2 h-auto">
                        <Bug className="mr-2 h-4 w-4" />
                        <span className="text-sm">Send Bug Report</span>
                    </SentryFeedbackButton>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign out</span>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
